import { useCallback, useState } from 'react';
import type { RangeSpanT } from '../../../../../../../common/types/types';
import { useSamForTracking } from '../../../../../providers/hooks/use-sam-for-tracking';
import { getUpdatedAnnotationSequence } from '../../utils';
type Range = {
  start: number;
  end: number;
};
type UseSequenceActionsProps = {
  annotationId: string;
  hasManualKeyFrameInRange: (start: number, end: number) => boolean;
  annotationSequence: Map<string, RangeSpanT>;
  onChange: (annotationSequence: Map<string, RangeSpanT>, isAnnotationTracking?: boolean) => Promise<void> | void;
  setTrackedSequence?: (sequence: {
    start: number;
    end: number;
    annotationId: string;
  } | null) => void;
  newRange: Range;
};
export const useSequenceActions = ({
  annotationId,
  hasManualKeyFrameInRange,
  annotationSequence,
  onChange,
  setTrackedSequence,
  newRange
}: UseSequenceActionsProps) => {
  const [showInterpolateOverrideModal, setShowInterpolateOverrideModal] = useState(false);
  const [pendingInterpolateRange, setPendingInterpolateRange] = useState<Range | null>(null);
  const [isInterpolating, setIsInterpolating] = useState(false);
  const {
    showOverrideModal,
    isTracking,
    startTracking,
    confirmOverride,
    cancelOverride
  } = useSamForTracking({
    trackingRange: {
      start: newRange.start + 1,
      end: newRange.end
    },
    hasConflicts: range =>
    // checking conflicts for start frame because tracking always makes an api call from next frame
    hasManualKeyFrameInRange(range.start, range.end),
    onTrack: async range => {
      const {
        updatedAnnotationSequence
      } = getUpdatedAnnotationSequence({
        start: range.start,
        end: range.end
      }, annotationSequence);
      await onChange(updatedAnnotationSequence, true);
    },
    onTrackSuccess: range => {
      setTrackedSequence?.({
        ...range,
        annotationId
      });
    },
    onTrackError: () => {
      setTrackedSequence?.(null);
    }
  });
  const handleTrackNextFrames = useCallback(() => {
    setTrackedSequence?.(null);
    startTracking();
  }, [startTracking, setTrackedSequence]);
  const handleConfirmTrackOverride = useCallback(() => {
    confirmOverride();
  }, [confirmOverride]);
  const handleCancelTrackOverride = useCallback(() => {
    cancelOverride();
  }, [cancelOverride]);
  const executeInterpolate = useCallback(async (range: Range) => {
    const {
      updatedAnnotationSequence
    } = getUpdatedAnnotationSequence({
      start: range.start,
      end: range.end
    }, annotationSequence);
    setIsInterpolating(true);
    try {
      await onChange(updatedAnnotationSequence, false);
    } finally {
      setIsInterpolating(false);
      setPendingInterpolateRange(null);
    }
  }, [annotationSequence, onChange]);
  const handleInterpolate = useCallback(async () => {
    // checking conflicts from start frame + 1 because start frame is used for interpolating rest of the frames
    if (hasManualKeyFrameInRange(newRange.start + 1, newRange.end)) {
      setPendingInterpolateRange(newRange);
      setShowInterpolateOverrideModal(true);
    } else {
      await executeInterpolate(newRange);
    }
  }, [newRange, hasManualKeyFrameInRange, executeInterpolate]);
  const handleConfirmInterpolateOverride = useCallback(async () => {
    if (pendingInterpolateRange) {
      setShowInterpolateOverrideModal(false);
      await executeInterpolate(pendingInterpolateRange);
    }
  }, [pendingInterpolateRange, executeInterpolate]);
  const handleCancelInterpolateOverride = useCallback(() => {
    setShowInterpolateOverrideModal(false);
    setPendingInterpolateRange(null);
  }, []);
  return {
    isTracking,
    isInterpolating,
    showTrackOverrideModal: showOverrideModal,
    showInterpolateOverrideModal,
    handleTrackNextFrames,
    handleConfirmTrackOverride,
    handleCancelTrackOverride,
    handleInterpolate,
    handleConfirmInterpolateOverride,
    handleCancelInterpolateOverride
  };
};